import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { Icon } from '@iconify/react';
import { LedgerMovement } from 'store/registry/useLedgerMovements';
import { useMovementOperations } from 'store/registry/useMovementOperations';
import { useCurrencyInput } from 'hooks/useCurrencyInput';
import intl from 'components/i18n/ReactIntlWrapper';
import { useAccountSearch, useAccount } from 'store/registry/useAccountManagement';
import { Account } from 'types/account';

interface MovementFormData {
  date: string;
  amount: number;
  currency: string;
  incoming: number; // 1 = incoming/credit, 0 = outgoing/debit
  description: string;
  movementType: 'regular' | 'transfer';
  idRelatedAccount: number | null;
  exchangeRate: number | null;
}

interface MovementFormDrawerProps {
  open: boolean;
  movement?: LedgerMovement | null;
  accountId: number;
  accountCurrency: string;
  onClose: () => void;
  onSuccess: () => void;
}

const MovementFormDrawer: React.FC<MovementFormDrawerProps> = ({
  open,
  movement,
  accountId,
  accountCurrency,
  onClose,
  onSuccess,
}) => {
  const isEditMode = !!movement;
  const { createMovement, updateMovement, prepareMovementData } = useMovementOperations();
  
  // Currency input hook for amount field
  const currencyInput = useCurrencyInput(0);

  // Account search for transfer destination
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [selectedRelatedAccount, setSelectedRelatedAccount] = useState<Account | null>(null);
  const { data: accountSearchResults = [], isFetching: accountSearchLoading } = useAccountSearch(
    accountSearchTerm,
    accountSearchTerm.length >= 2
  );

  // Fetch the related account by ID when editing a transfer movement
  const relatedAccountId = isEditMode && movement?.movementType === 'transfer' ? (movement.idRelatedAccount ?? 0) : 0;
  const { data: fetchedRelatedAccount } = useAccount(relatedAccountId, relatedAccountId > 0);

  // When the fetched related account arrives, set it as the selected account
  useEffect(() => {
    if (fetchedRelatedAccount && open && isEditMode) {
      setSelectedRelatedAccount(fetchedRelatedAccount);
    }
  }, [fetchedRelatedAccount, open, isEditMode]);
  
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<MovementFormData>({
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      amount: 0,
      currency: accountCurrency,
      incoming: 1,
      description: '',
      movementType: 'regular',
      idRelatedAccount: null,
      exchangeRate: null,
    },
  });

  const movementType = watch('movementType');
  const isTransfer = movementType === 'transfer';

  // Determine if currencies differ (transfer cross-currency)
  const relatedAccountCurrency = selectedRelatedAccount?.currency ?? null;
  const isCrossCurrency = isTransfer && relatedAccountCurrency !== null && relatedAccountCurrency !== accountCurrency;

  // Reset form when drawer opens/closes or movement changes
  useEffect(() => {
    if (open) {
      if (movement) {
        // Edit mode - populate with existing movement data
        const isTransferMovement = movement.movementType === 'transfer';
        reset({
          date: movement.date ? new Date(movement.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          amount: movement.amount,
          currency: movement.currency?.code || accountCurrency,
          incoming: movement.incoming,
          description: movement.description,
          movementType: isTransferMovement ? 'transfer' : 'regular',
          idRelatedAccount: movement.idRelatedAccount ?? null,
          exchangeRate: movement.exchangeRate ?? null,
        });
        currencyInput.setValue(movement.amount * 100);
        // For transfer movements, selectedRelatedAccount will be populated
        // by the useAccount fetch (via fetchedRelatedAccount useEffect above).
        // For non-transfers, clear it immediately.
        if (!isTransferMovement) {
          setSelectedRelatedAccount(null);
        }
      } else {
        // Create mode - use defaults
        reset({
          date: new Date().toISOString().split('T')[0],
          amount: 0,
          currency: accountCurrency,
          incoming: 1,
          description: '',
          movementType: 'regular',
          idRelatedAccount: null,
          exchangeRate: null,
        });
        currencyInput.setValue(0);
      }
      // Reset account search term (but not selectedRelatedAccount - handled per-branch above)
      setAccountSearchTerm('');
    }
  }, [open, movement, reset, accountCurrency, currencyInput.setValue]);

  const onSubmit = (data: MovementFormData) => {
    const movementData = prepareMovementData({
      ...data,
      amount: currencyInput.centValue / 100,
      movementType: data.movementType === 'transfer' ? 'transfer' : null,
      idRelatedAccount: data.movementType === 'transfer' ? selectedRelatedAccount?.id ?? null : null,
      exchangeRate: isCrossCurrency ? data.exchangeRate : null,
    }, accountId);

    if (isEditMode && movement) {
      updateMovement.mutate({
        id: movement.id,
        movementData,
      }, {
        onSuccess: () => {
          onSuccess();
          onClose();
        },
      });
    } else {
      createMovement.mutate(movementData, {
        onSuccess: () => {
          onSuccess();
          onClose();
        },
      });
    }
  };

  const handleClose = () => {
    const isSubmitting = createMovement.isPending || updateMovement.isPending;
    if (!isSubmitting) {
      reset();
      onClose();
    }
  };

  const validateDate = (value: string) => {
    if (!value) {
      return intl.get('dateIsRequired');
    }
    return true;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 500 }, p: 3 },
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Header */}
        <Typography variant="h5" gutterBottom>
          {isEditMode ? intl.get('accountDetail.editMovement') : intl.get('accountDetail.newMovement')}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {isEditMode
            ? intl.get('accountDetail.updateMovementInformation')
            : intl.get('accountDetail.addNewMovementToAccount')}
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Stack spacing={3}>

          {/* Movement Type toggle — only on create */}
          {!isEditMode && (
            <Controller
              name="movementType"
              control={control}
              render={({ field }) => (
                <FormControl>
                  <FormLabel sx={{ mb: 1 }}>{intl.get('movements.movementType')}</FormLabel>
                  <ToggleButtonGroup
                    value={field.value}
                    exclusive
                    onChange={(_, val) => { if (val) field.onChange(val); }}
                    size="small"
                  >
                    <ToggleButton value="regular">
                      <Icon icon="mdi:swap-vertical" style={{ marginRight: 6 }} />
                      {intl.get('movements.regular')}
                    </ToggleButton>
                    <ToggleButton value="transfer">
                      <Icon icon="mdi:bank-transfer" style={{ marginRight: 6 }} />
                      {intl.get('movements.transfer')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </FormControl>
              )}
            />
          )}

          {/* Date */}
          <Controller
            name="date"
            control={control}
            rules={{ validate: validateDate }}
            render={({ field }) => (
              <TextField
                {...field}
                label={intl.get('movements.date')}
                type="date"
                required
                fullWidth
                error={!!errors.date}
                helperText={errors.date?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          {/* Amount */}
          <Controller
            name="amount"
            control={control}
            rules={{
              required: intl.get('errorcodes.amountrequired'),
              validate: () => currencyInput.centValue > 0 || intl.get('errorcodes.amountMustBeGreaterThanZero'),
            }}
            render={({ field }) => (
              <TextField
                label={intl.get('accountManagement.amount')}
                fullWidth
                required
                inputProps={{ style: { textAlign: 'right' } }}
                value={currencyInput.displayValue}
                onChange={(e) => {
                  currencyInput.handleChange(e.target.value);
                  field.onChange(currencyInput.centValue / 100);
                }}
                placeholder="0.00"
                error={!!errors.amount}
                helperText={errors.amount?.message}
              />
            )}
          />

          {/* Currency (read-only) */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {intl.get('accountManagement.currency')}
            </Typography>
            <Chip label={accountCurrency} variant="outlined" sx={{ mt: 0.5 }} />
          </Box>

          {/* Transaction Type */}
          <Controller
            name="incoming"
            control={control}
            rules={{ required: intl.get('movements.transactionTypeIsRequired') }}
            render={({ field }) => (
              <FormControl component="fieldset" error={!!errors.incoming}>
                <FormLabel component="legend" required>
                  {intl.get('movements.transactionType')}
                </FormLabel>
                <RadioGroup
                  {...field}
                  value={field.value.toString()}
                  onChange={(e) => field.onChange(parseInt(e.target.value))}
                  row
                >
                  <FormControlLabel
                    value="1"
                    control={<Radio />}
                    label={<Box display="flex" alignItems="center" gap={1}>{intl.get('accountManagement.credit')}</Box>}
                  />
                  <FormControlLabel
                    value="0"
                    control={<Radio />}
                    label={<Box display="flex" alignItems="center" gap={1}>{intl.get('accountManagement.debit')}</Box>}
                  />
                </RadioGroup>
                {errors.incoming && <FormHelperText>{errors.incoming.message}</FormHelperText>}
              </FormControl>
            )}
          />

          {/* Transfer fields — only shown when movementType === 'transfer' */}
          {isTransfer && (
            <>
              <Divider />

              {/* Destination Account search */}
              <Autocomplete
                options={accountSearchResults.filter(a => a.id !== accountId)}
                getOptionLabel={(option) => `${option.accountNumber} — ${option.accountName}`}
                loading={accountSearchLoading}
                inputValue={accountSearchTerm}
                value={selectedRelatedAccount}
                disabled={isEditMode}
                onInputChange={(_, val) => setAccountSearchTerm(val)}
                onChange={(_, val) => {
                  setSelectedRelatedAccount(val);
                }}
                noOptionsText={
                  accountSearchTerm.length < 2
                    ? intl.get('movements.typeToSearch')
                    : intl.get('movements.noAccountsFound')
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={intl.get('movements.destinationAccount')}
                    required
                    error={isTransfer && !selectedRelatedAccount}
                    helperText={
                      isTransfer && !selectedRelatedAccount
                        ? intl.get('errorcodes.relatedaccountrequired')
                        : intl.get('movements.destinationAccountHelp')
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {accountSearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {/* Cross-currency notice + exchange rate */}
              {selectedRelatedAccount && (
                <>
                  {isCrossCurrency ? (
                    <>
                      <Alert severity="info">
                        {intl.get('movements.crossCurrencyNotice')
                          .replace('{from}', accountCurrency)
                          .replace('{to}', relatedAccountCurrency!)}
                      </Alert>
                      <Controller
                        name="exchangeRate"
                        control={control}
                        rules={{
                          required: isCrossCurrency ? intl.get('errorcodes.exchangeraterequired') : false,
                          min: { value: 0.000001, message: intl.get('errorcodes.exchangeRateMustBePositive') },
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={intl.get('movements.exchangeRate')}
                            type="number"
                            required
                            fullWidth
                            inputProps={{ step: '0.000001', min: '0' }}
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                            error={!!errors.exchangeRate}
                            helperText={
                              errors.exchangeRate?.message ||
                              intl.get('movements.exchangeRateHelp')
                                .replace('{from}', accountCurrency)
                                .replace('{to}', relatedAccountCurrency!)
                            }
                          />
                        )}
                      />
                    </>
                  ) : (
                    <Alert severity="success" icon={<Icon icon="mdi:check-circle" />}>
                      {intl.get('movements.sameCurrencyTransfer').replace('{currency}', accountCurrency)}
                    </Alert>
                  )}
                </>
              )}
            </>
          )}

          {/* Description */}
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={intl.get('accountManagement.description')}
                fullWidth
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description?.message || intl.get('accountManagement.descriptionHelp')}
              />
            )}
          />
        </Stack>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={createMovement.isPending || updateMovement.isPending}
          >
            {intl.get('common.cancel')}
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={
              (createMovement.isPending || updateMovement.isPending) ||
              (isTransfer && !selectedRelatedAccount)
            }
          >
            {intl.get('save.button')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default MovementFormDrawer;